/**
 * StripeReconciliationWidget
 * --------------------------
 * Shows every Stripe subscription that ISN'T fully tracked in our DB
 * (no matching user, or no matching subscriptions row, or email drift)
 * with one-click "Import" buttons.
 *
 * Owner spec 2026-02-17 — "Sam Schwartz wasn't on the list. I added a
 * sub. We need to reconcile those." This widget exposes that drift
 * surface and the import path.
 *
 * Backend:
 *   GET  /api/coach/billing/stripe-reconciliation
 *   POST /api/coach/billing/stripe-import
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    AlertTriangle, CheckCircle2, Loader2, RefreshCw, Plus,
    UserPlus, FileWarning, Mail,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (cents) => cents == null ? "—" : `$${(cents / 100).toFixed(2)}`;

const KIND_META = {
    missing_user: {
        label: "Not in MaxSC",
        tone: "border-red-700/60 bg-red-950/30 text-red-200",
        icon: UserPlus,
    },
    missing_sub: {
        label: "User exists, sub not tracked",
        tone: "border-amber-700/60 bg-amber-950/30 text-amber-200",
        icon: FileWarning,
    },
    email_drift: {
        label: "Email mismatch",
        tone: "border-zinc-700 bg-zinc-900/40 text-zinc-200",
        icon: Mail,
    },
};

function DriftRow({ row, onImported }) {
    const [busy, setBusy] = useState(false);
    const meta = KIND_META[row.kind] || KIND_META.missing_user;
    const Icon = meta.icon;

    const doImport = async () => {
        const verb = row.kind === "missing_user"
            ? `Create a new MaxSC user for "${row.customer_name || row.customer_email}" and link this Stripe subscription?`
            : `Import this Stripe subscription into ${row.local_user_name || row.local_user_email || "the existing user"}'s account?`;
        if (!window.confirm(`${verb}\n\nThis is safe and reversible.`)) return;
        setBusy(true);
        try {
            const r = await axios.post(
                `${API}/coach/billing/stripe-import`,
                {
                    stripe_subscription_id: row.stripe_subscription_id,
                    stripe_customer_id: row.stripe_customer_id,
                },
                { withCredentials: true },
            );
            toast.success(
                r.data.user_created
                    ? `✓ Created user + imported sub (${r.data.email})`
                    : `✓ Linked existing user + ${r.data.sub_created ? "created sub" : "updated sub"}`,
                { duration: 8000 },
            );
            onImported?.();
        } catch (e) {
            toast.error("Import failed", { description: e?.response?.data?.detail || "Server error" });
        } finally { setBusy(false); }
    };

    return (
        <div
            data-testid={`recon-row-${row.stripe_subscription_id}`}
            className={`border ${meta.tone} p-3 mb-2`}
        >
            <div className="flex items-start gap-2">
                <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="text-[10px] tracking-[0.18em] uppercase font-semibold">
                            {meta.label}
                        </span>
                        <span className="text-[10px] text-zinc-400 uppercase">{row.status}</span>
                    </div>
                    <div className="text-sm mt-1.5 text-white">
                        {row.customer_name || "(no name)"} · {row.customer_email || "(no email)"}
                    </div>
                    <div className="text-[10px] text-zinc-400 mt-1 font-mono break-all">
                        {row.stripe_subscription_id} · {fmtMoney(row.price_cents)}/{row.interval || "?"}
                    </div>
                    {row.local_user_id && (
                        <div className="text-[10px] text-zinc-500 mt-1">
                            existing user: {row.local_user_name} ({row.local_user_email}) · matched by {row.matched_by}
                        </div>
                    )}
                    <div className="text-xs text-zinc-300 mt-1.5">{row.detail}</div>
                    <button
                        type="button"
                        data-testid={`recon-import-${row.stripe_subscription_id}`}
                        onClick={doImport}
                        disabled={busy}
                        className="mt-2 text-[10px] tracking-[0.18em] uppercase border border-brand-red bg-brand-red/10 text-brand-red hover:bg-brand-red hover:text-white px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-50 transition-colors"
                    >
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                        {busy ? "Importing…" : row.kind === "missing_user" ? "Create user + import" : "Import"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function StripeReconciliationWidget() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await axios.get(
                `${API}/coach/billing/stripe-reconciliation`,
                { withCredentials: true },
            );
            setData(r.data);
        } catch (e) {
            toast.error("Reconciliation scan failed", {
                description: e?.response?.data?.detail || "Server error",
            });
        } finally { setLoading(false); }
    }, []);

    useEffect(() => {
        if (open && !data && !loading) load();
    }, [open, data, loading, load]);

    const driftCount = data?.drift?.length || 0;
    const synced = data?.synced_count || 0;

    return (
        <section
            data-testid="stripe-recon-widget"
            className="border border-zinc-800 bg-zinc-950/40 p-5 rounded-lg"
        >
            <button
                type="button"
                data-testid="stripe-recon-toggle"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-2 text-left"
            >
                <div className="flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 text-zinc-400" />
                    <span className="text-sm tracking-[0.18em] uppercase text-zinc-200 font-display font-bold">
                        Stripe ↔ MaxSC reconciliation
                    </span>
                    {data && driftCount > 0 && (
                        <span
                            data-testid="stripe-recon-drift-badge"
                            className="text-[10px] tracking-[0.15em] uppercase bg-red-900/60 text-red-200 px-2 py-0.5 border border-red-700/60"
                        >
                            {driftCount} to reconcile
                        </span>
                    )}
                    {data && driftCount === 0 && (
                        <span className="text-[10px] tracking-[0.15em] uppercase bg-emerald-900/40 text-emerald-300 px-2 py-0.5 border border-emerald-700/40">
                            All synced ({synced})
                        </span>
                    )}
                </div>
                <span className="text-[10px] text-zinc-500">{open ? "Hide" : "Scan Stripe"}</span>
            </button>
            {open && (
                <div className="mt-3">
                    {loading && (
                        <div className="text-xs text-zinc-400 flex items-center gap-2">
                            <Loader2 className="w-3 h-3 animate-spin" /> Walking every Stripe subscription…
                        </div>
                    )}
                    {!loading && data && (
                        <>
                            <div className="grid grid-cols-4 gap-2 text-[10px] uppercase tracking-[0.18em] mb-3 border border-zinc-800">
                                <div className="p-2 border-r border-zinc-800">
                                    <div className="text-zinc-500">Stripe total</div>
                                    <div className="text-base text-white font-mono">{data.total_stripe_subs}</div>
                                </div>
                                <div className="p-2 border-r border-zinc-800">
                                    <div className="text-zinc-500">Synced</div>
                                    <div className="text-base text-emerald-300 font-mono">{data.synced_count}</div>
                                </div>
                                <div className="p-2 border-r border-zinc-800">
                                    <div className="text-zinc-500">Missing user</div>
                                    <div className="text-base text-red-300 font-mono">{data.summary?.missing_users || 0}</div>
                                </div>
                                <div className="p-2">
                                    <div className="text-zinc-500">Missing sub</div>
                                    <div className="text-base text-amber-300 font-mono">{data.summary?.missing_subs || 0}</div>
                                </div>
                            </div>
                            {driftCount === 0 ? (
                                <div
                                    data-testid="stripe-recon-clean"
                                    className="border border-emerald-700/40 bg-emerald-900/10 text-emerald-200 px-3 py-2.5 text-xs flex items-center gap-2"
                                >
                                    <CheckCircle2 className="w-4 h-4" />
                                    Stripe and MaxSC fully reconciled — every active Stripe subscription has a matching user and sub row in our DB.
                                </div>
                            ) : (
                                <>
                                    <div className="flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-amber-300 mb-2">
                                        <AlertTriangle className="w-3.5 h-3.5" />
                                        Drift detected — click Import on each row to reconcile
                                    </div>
                                    {data.drift.map((d) => (
                                        <DriftRow key={d.stripe_subscription_id} row={d} onImported={load} />
                                    ))}
                                </>
                            )}
                            <button
                                type="button"
                                data-testid="stripe-recon-refresh"
                                onClick={load}
                                disabled={loading}
                                className="mt-2 text-[10px] tracking-[0.15em] uppercase text-zinc-400 hover:text-white"
                            >
                                ↻ Re-scan Stripe
                            </button>
                        </>
                    )}
                </div>
            )}
        </section>
    );
}
